define('presentation/settings/webChats/viewModels/webChatWidgetsViewModel',[
    'businessServices/authentication/accountPlanInfo',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/events/eventManager',
    'businessServices/router/router',
    'common/collections/collectionFilter',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'constants/scrollEventSources',
    'presentation/common/actionModal/viewModels/actionModalViewModel',
    'presentation/common/actionModal/viewModels/deleteWebChatWidgetActionViewModel',
    'presentation/settings/webChats/facades/webChatWidgetsFacade',
    'presentation/settings/webChats/viewModels/noWebChatPackageModalViewModel',
    'settings/navigationConfiguration'
], function(){
    return function() {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const DeleteWebChatWidgetActionViewModelConstructor = require('presentation/common/actionModal/viewModels/deleteWebChatWidgetActionViewModel');
        const NoWebChatPackageModalConstructor = require('presentation/settings/webChats/viewModels/noWebChatPackageModalViewModel');

        const FilterConstructor = require('common/collections/collectionFilter');
        const SorterConstructor = require('common/collections/collectionSorter');

        /** @type typeof import('constants/scrollEventSources') */
        const ScrollEventSources = require('constants/scrollEventSources');

        const _accountPlanInfo = require('businessServices/authentication/accountPlanInfo');
        const _commonState = require('common/storage/commonState');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');

        let _disposables = [];
        let _eventManager = null;
        let _facade = null;
        let _getNextBatchCalled = false;
        let _getNextBatchOfWebChatWidgetsEventId = null;

        const DELETE_RESPONSE = "yes";
        const ROWS_PER_PAGE = 20;

        let _state = {
            availableWebChatWidgets: ko.observableArray([]),
            filteredWebChatWidgets: [],
            webChatWidgetsShown: 0
        };

        const _showDeleteWidget = (widgetPresentationObject) => {
            const webChatWidgetId = widgetPresentationObject.webChatWidgetId;
            _buildDeleteModal(widgetPresentationObject);
            self.actionModal.showModal()
                .done((result) => {
                    if (result === DELETE_RESPONSE) {
                        _facade.deleteWebChatWidget(webChatWidgetId)
                        .fail((error) => {
                            error.webChatWidgetId = webChatWidgetId;
                            throw error;
                        })
                        .done(() => {
                            self.displayWebChatWidgets.remove(widgetPresentationObject);
                            _state.filteredWebChatWidgets = _state.filteredWebChatWidgets.filter((widget) => {
                                return widget.webChatWidgetId !== webChatWidgetId;
                            });
                            _state.availableWebChatWidgets(_state.availableWebChatWidgets().filter((widget) => {
                                return widget.webChatWidgetId !== webChatWidgetId;
                            }));
                            if (self.displayWebChatWidgets().length < ROWS_PER_PAGE) {
                                _getNextBatchOfWebChatWidgets();
                            }
                        });
                    }
                });
        };

        const _showMissingWebChatPackage = () => {
            _buildNoWebChatPackageModal();
            self.actionModal.showModal();
        };

        const _buildDeleteModal = (webChatWidget) => {
            const displayType = _commonState.typeDisplay.webChatWidget;
            let constructorParams = [webChatWidget];
            self.actionModal
                .clearModal()
                .setContentViewModel(DeleteWebChatWidgetActionViewModelConstructor, constructorParams)
                .setHeaderText({i18n: {key: 'deleteHeader', options: {type: displayType}}})
                .setSubmitButtonText({i18n: 'delete'});
        };

        const _buildNoWebChatPackageModal = () => {
            const constructorParams = [_sessionAccountInfo.isBillingAdmin()];
            self.actionModal
                .clearModal()
                .setContentViewModel(NoWebChatPackageModalConstructor, constructorParams)
                .setHeaderText({i18n: `webChatWidgets:addWebChat`})
                .setShouldDisplayCancelButton(false)
                .setSubmitButtonText({i18n: 'ok'})
                .setShouldDisplayDefaultFooterMessage(true);
        };

        const _maybeAddToFilteredWebChatWidgets = (filteredItems, webChatWidget, filteredWebChatWidgets) => {
            if (filteredItems.length > 0) {
                if (filteredWebChatWidgets.every(filteredWebChatWidget => {
                    return filteredWebChatWidget.webChatWidgetId !== webChatWidget.webChatWidgetId;
                })) {
                    filteredWebChatWidgets.push(webChatWidget);
                }
            }
        };

        const _updateDisplayedWebChatWidgets = () => {
            let filteredWebChatWidgets;
            let filterContent = self.webChatWidgetFilter();
            if (filterContent !== '') {
                let webChatWidgetsFilter = new FilterConstructor();
                webChatWidgetsFilter.addProperty('displayName');
                webChatWidgetsFilter.addProperty('modifiedDateTime.filterValue');
                webChatWidgetsFilter.addProperty('createdDateTime.filterValue');
                webChatWidgetsFilter.addProperty('domain');
                webChatWidgetsFilter.addValue(filterContent);
                filteredWebChatWidgets = webChatWidgetsFilter.filter(_state.availableWebChatWidgets());

                _state.availableWebChatWidgets().forEach(webChatWidget => {
                    let authorizedDomainsToFilter = webChatWidget.authorizedDomains;
                    let authorizedDomainItems = webChatWidgetsFilter.filter(authorizedDomainsToFilter);
                    _maybeAddToFilteredWebChatWidgets(authorizedDomainItems, webChatWidget, filteredWebChatWidgets);
                });
                _state.filteredWebChatWidgets = filteredWebChatWidgets;
            }
            else {
                filteredWebChatWidgets = _state.availableWebChatWidgets();
                _state.filteredWebChatWidgets = filteredWebChatWidgets;
            }
        };

        const _refreshWebChats = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getWebChatWidgets()
                    .fail(promise.reject)
                    .done((webChats) => {
                        _state.availableWebChatWidgets(webChats);
                        _state.filteredWebChatWidgets = webChats;
                        _state.webChatWidgetsShown = webChats.length;
                        _sortDisplayedWebChatWidgets();
                        if (_getNextBatchOfWebChatWidgetsEventId === null) {
                            _getNextBatchOfWebChatWidgetsEventId = _eventManager.subscribeBottomScrollBufferReached(_getNextBatchOfWebChatWidgets);
                        }
                        promise.resolve();

                    });
            });
        };

        const _sortDisplayedWebChatWidgets = () => {
            let sorter = new SorterConstructor();
            if (sorter.setOptions(self.sortOptions(), self.sortType())) {
                sorter.sort(_state.availableWebChatWidgets);
                _updateDisplayedWebChatWidgets();
                self.lastPage = false;
                _state.webChatWidgetsShown = ROWS_PER_PAGE;
                self.displayWebChatWidgets(_state.filteredWebChatWidgets.slice(0, _state.webChatWidgetsShown));
            }
        };

        const _getNextBatchOfWebChatWidgets = (eventSource) => {
            if (eventSource !== self.scrollEventSource) {
                return;
            }

            if (self.lastPage === false && _getNextBatchCalled === false){
                _getNextBatchCalled = true;
                let additionalWebChatWidgetsShown = _state.webChatWidgetsShown + ROWS_PER_PAGE;
                let additionalWebChatWidgets = _state.filteredWebChatWidgets.slice(_state.webChatWidgetsShown, additionalWebChatWidgetsShown);
                self.displayWebChatWidgets.push.apply(self.displayWebChatWidgets, additionalWebChatWidgets);
                _state.webChatWidgetsShown = additionalWebChatWidgetsShown;
                if (_state.webChatWidgetsShown >= _state.filteredWebChatWidgets.length) {
                    self.lastPage = true;
                }
                _getNextBatchCalled = false;
            }
        };

        const _webChatWidgetFilterOnChange = () =>{
            _updateDisplayedWebChatWidgets();
            self.lastPage = false;
            _state.webChatWidgetsShown = ROWS_PER_PAGE;
            self.displayWebChatWidgets(_state.filteredWebChatWidgets.slice(0, _state.webChatWidgetsShown));
        };

        self.scrollEventSource = ScrollEventSources.settingsWebChatWidgets;
        self.actionModal = null;
        self.lastPage = false;
        self.isCompositionComplete = ko.observable(false);
        self.displayWebChatWidgets = ko.observableArray([]);
        self.addWebChatWidgetUrl = _navigationConfiguration.routesById.addWebChatWidget.url;

        self.sortType = ko.observable("displayName");
        _disposables.push(self.sortType.subscribe(_sortDisplayedWebChatWidgets));
        self.sortOptions = ko.observableArray([
            {textI18n: "name", iconName: 'sortName', sortPath : "displayName", isAscending : true},
            {textI18n: "recentlyAdded", iconName: 'sortRecentlyAdded', sortPath : "createdDateTime.sortValue", isAscending : false},
            {textI18n: "recentlyModified", iconName: 'sortRecentlyModified', sortPath : "updatedDateTime.sortValue", isAscending : false}
        ]);
        self.webChatWidgetFilter = ko.observable('');
        _disposables.push(self.webChatWidgetFilter.subscribe(_webChatWidgetFilterOnChange));

        self.showResults = ko.pureComputed(() => self.displayWebChatWidgets().length > 0);
        self.emptyStateContentKey = ko.pureComputed(() => _state.availableWebChatWidgets().length > 0 ?
            "webChatWidgets:noResults" :
            "webChatWidgets:noWebChats");

        self.isImpersonating = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.addWebChatWidget = () => {
            if (_accountPlanInfo.hasWebChatPackage()) {
                _router.navigate(self.addWebChatWidgetUrl);
            } else {
                _showMissingWebChatPackage();
            }
        };

        self.deleteWebChatWidget = (webChatWidget) => {
            return _promiseFactory.deferIndefinitely(promise => {
                _showDeleteWidget(webChatWidget);
                promise.resolve();
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _disposables.forEach(s => s.dispose);
            _disposables = [];
            _eventManager.unsubscribe(_getNextBatchOfWebChatWidgetsEventId);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/settings/webChats/facades/webChatWidgetsFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ActionModalViewModelConstructor = require('presentation/common/actionModal/viewModels/actionModalViewModel');
            self.actionModal = new ActionModalViewModelConstructor();

            _eventManager = require('businessServices/events/eventManager');

            return _initialize();
        };

        const _initialize = () => {
            _refreshWebChats();
            return _promiseFactory.wait();
        };
    };
});

